import {
  AiAnalysis,
  AiChecklistItem,
} from "../../../services/sheai/SheAiChecklistTypes";

/**
 * O "Relatório de análise da estação": a leitura CURADA da análise, por eixo.
 *
 * A aba Dados IA tinha só o rastro técnico — item a item, critério a critério,
 * tokens. Serve para auditar o agente, não para o professor entender o
 * desempenho. Este módulo produz a outra leitura, agrupada por Habilidade:
 * o que o avaliado acertou e o que precisa melhorar. Sem nota — a pontuação
 * saiu da aba por decisão do cliente; quem pontua é o professor na aba
 * Checklist.
 *
 * DERIVADO, não gerado: `acertos` e `melhorar` saem de `items[].result` +
 * `justification`, exatamente como o `dna_writer._build_relatorio_docx` do
 * agente monta o .docx. Mesma regra, dois lugares — é dívida conhecida, e a
 * saída é o agente passar a serializar `report` no JSON (então este módulo
 * deixa de derivar e passa a só ler). Enquanto isso, `objetivo` e
 * `proximoPasso` só aparecem quando o agente os manda: são julgamento
 * pedagógico, não dá para inferir do veredito.
 */

/** Rótulo de cada classe dentro de "Pontos a melhorar", como no .docx. */
const TERMO_CLASSE: Record<string, string> = {
  partially_done: "fez parcialmente",
  done_wrong: "fez de forma incorreta",
  not_done: "não fez",
};

export interface ApontamentoDoEixo {
  itemId: number | string;
  /** O enunciado do Item — o que foi avaliado. */
  pergunta: string;
  /** A justificativa da IA para o veredito. */
  observacao: string;
  /** Rótulo da classe, presente só em "melhorar" ("não fez", …). */
  classe?: string;
  /** Primeiro trecho de evidência, para o timestamp clicável. */
  ts?: string;
  sourceId?: string;
  critico: boolean;
}

export interface EixoDoRelatorio {
  ordem: number;
  nome: string;
  /** Objetivo do eixo — só quando o agente o envia (`report.eixos[].objetivo`). */
  objetivo?: string;
  acertos: ApontamentoDoEixo[];
  melhorar: ApontamentoDoEixo[];
  /** Recomendação pedagógica — só quando o agente a envia. */
  proximoPasso?: string;
  /** Itens eliminatórios que a IA classificou como não cumpridos. */
  criticosFalhados: string[];
}

/**
 * Enunciado do Item. Vem do checklist estruturado quando a tela o tem; senão
 * do próprio payload da análise, onde a convenção `checklist_spec` prefixa a
 * pergunta com `[HABILIDADE] `.
 */
export function perguntaDoItem(
  item: AiChecklistItem,
  perguntaPorItem?: Record<string, string>
): string {
  const daTela = perguntaPorItem?.[String(item.item_id)];
  if (daTela) return daTela;
  const bruta = item.question ?? "";
  return bruta.replace(/^\[[^\]]+\]\s*/, "").trim() || `Item ${item.item_id}`;
}

function apontamento(
  item: AiChecklistItem,
  perguntaPorItem: Record<string, string> | undefined,
  comClasse: boolean
): ApontamentoDoEixo {
  return {
    itemId: item.item_id,
    pergunta: perguntaDoItem(item, perguntaPorItem),
    observacao: (item.justification || "").trim(),
    classe: comClasse ? TERMO_CLASSE[item.result] ?? item.result : undefined,
    ts: item.evidence?.[0]?.ts_start,
    sourceId: item.evidence?.[0]?.source_id,
    critico: !!item.critical,
  };
}

export function eixosDoRelatorio(
  analise: AiAnalysis | null,
  perguntaPorItem?: Record<string, string>
): EixoDoRelatorio[] {
  const itens = analise?.items || [];
  if (itens.length === 0) return [];

  // Ordem das Habilidades: a do checklist quando a análise a declara (é a
  // ordem que o professor escreveu), senão a de primeira aparição dos itens.
  const declaradas = (analise?.scoring?.skills || []).map((s) => s.name);
  const ordem: string[] = [...declaradas];
  itens.forEach((item) => {
    const nome = item.skill || "Geral";
    if (!ordem.includes(nome)) ordem.push(nome);
  });

  const curado = analise?.report?.eixos || [];

  const eixos: EixoDoRelatorio[] = [];

  ordem.forEach((nome) => {
    const doEixo = itens.filter((item) => (item.skill || "Geral") === nome);
    if (doEixo.length === 0) return;

    const curadoDoEixo = curado.find((e) => e.nome === nome);

    eixos.push({
      // A numeração é a POSIÇÃO no relatório, não o id da Habilidade: eixo
      // declarado no checklist sem item avaliado não aparece e não gasta número.
      ordem: eixos.length + 1,
      nome,
      objetivo: curadoDoEixo?.objetivo,
      proximoPasso: curadoDoEixo?.proximo_passo,
      acertos: doEixo
        .filter((item) => item.result === "done")
        .map((item) => apontamento(item, perguntaPorItem, false)),
      melhorar: doEixo
        .filter((item) => item.result !== "done")
        .map((item) => apontamento(item, perguntaPorItem, true)),
      criticosFalhados: doEixo
        .filter(
          (item) =>
            item.critical &&
            (item.result === "not_done" || item.result === "done_wrong")
        )
        .map((item) => perguntaDoItem(item, perguntaPorItem)),
    });
  });

  return eixos;
}


